import React from 'react';
import { Button } from '../ui/button';

interface PlanData {
  name: string;
  monthlyPrice: number;
  quarterlyPrice: number;
  quarterlyTotal: number;
  features: string[];
  icon: React.ReactNode;
}

interface QuarterlyPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlan: PlanData;
  onSelectMonthly: () => void;
  onSelectQuarterly: () => void;
}

export const QuarterlyPlanModal: React.FC<QuarterlyPlanModalProps> = ({
  isOpen,
  onClose,
  selectedPlan,
  onSelectMonthly,
  onSelectQuarterly
}) => {
  if (!isOpen) return null;

  const savings = Math.round((selectedPlan.monthlyPrice * 3 - selectedPlan.quarterlyTotal) * 100) / 100;
  
  const getSavingsAmount = (planName: string) => {
    const name = planName.toLowerCase();
    if (name === 'hobby') return '$15';
    if (name === 'starter') return '$30';
    if (name === 'expert') return '$51';
    return `$${savings}`;
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-3xl w-[calc(100%-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-8 pb-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-blue-600 mb-4">
            Save 25% with Quarterly Billing
          </h2>
          <p className="text-base text-gray-600 mx-auto whitespace-normal md:whitespace-nowrap">
            Secure a better deal with quarterly.
            <br className="md:hidden" />
            {" "}Start creating viral clips today.
          </p>
        </div>

        {/* Plan Comparison */}
        <div className="px-6 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            
            {/* Monthly Plan (Left) */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 relative flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-600 tracking-wide h-8">
                  {selectedPlan.name.charAt(0).toUpperCase() + selectedPlan.name.slice(1).toLowerCase()}
                </h3>
                
                <div className="mt-4">
                  <div className="h-14 flex items-center justify-center">
                    <span className="text-5xl font-bold text-gray-900">
                      ${selectedPlan.monthlyPrice}
                    </span>
                  </div>
                  <div className="text-gray-600 mt-2">
                    Monthly
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6 flex-grow">
                <div className="flex items-center text-gray-600">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                  <span>Month to month billing</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                  <span>Standard price for same features</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                  <span>Cancel anytime</span>
                </div>
              </div>

              <Button
                onClick={onSelectMonthly}
                variant="outline"
                className="w-full h-12 text-blue-600 border-blue-600 hover:bg-blue-50 rounded-full"
              >
                Continue with Monthly
              </Button>
            </div>

            {/* Quarterly Plan (Right) - Highlighted */}
            <div className="bg-white rounded-2xl border-4 border-blue-500 p-6 relative shadow-lg flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-blue-600 h-8">
                  Save 25%!
                </h3>
                
                <div className="mt-4">
                  <div className="h-14 flex items-center justify-center">
                    <span className="text-5xl font-bold text-gray-900">
                      ${selectedPlan.quarterlyPrice}
                    </span>
                    <span className="text-lg text-gray-500 line-through ml-2">
                      ${selectedPlan.monthlyPrice}
                    </span>
                  </div>
                  <div className="text-gray-600 mt-2">
                    Monthly
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6 flex-grow">
                <div className="flex items-center text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span>Flexible quarterly billing</span>
                </div>
                <div className="flex items-center text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span>Save over {getSavingsAmount(selectedPlan.name)} over 3 months</span>
                </div>
                <div className="flex items-center text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span>Cancel anytime, no hidden fees</span>
                </div>
              </div>

              <Button
                onClick={onSelectQuarterly}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full"
              >
                Save 25%
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};